import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  type Campaign,
  ApiError,
  api,
  ago,
  type Report,
  type ReportSection,
  type ReportSpec,
  type ReportStyle,
  type SectionKind,
} from "../api";
import { useTitle } from "../useTitle";
import { useIsAdmin } from "../session";
import { Board, Loading, Notice, PageHead } from "../components/Look";
import Modal from "../components/Modal";
import { type Bench } from "../api";

/**
 * Compose a report instead of picking one of five levels.
 *
 * The bench builds a document at `summary|results|failures|standard|full` - one
 * dial for the whole page - so "the failures in full, everything else as a
 * list" cannot be asked for, and nothing can span two runs. A release document
 * is cross-run by nature, which is why people were assembling it by hand.
 *
 * The editor offers exactly what the server accepts, because the section list
 * arrives FROM the server. A form built from a second copy of that list offers
 * sections that are then refused on save, and the person doing the refusing is
 * the one who trusted the form.
 */

const BLANK: ReportSpec = {
  scope: { benches: [], runs: { last: 1 }, scenarios: "", verdicts: [] },
  sections: [{ type: "summary", detail: "counts" }],
};

/** A date as the browser's date input wants it, and as the spec stores it. */
function DateBox({ value, onChange }: { value?: number; onChange: (v?: number) => void }) {
  const asText = value ? new Date(value * 1000).toISOString().slice(0, 10) : "";
  return (
    <input
      type="date"
      className="ml-2 rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
      value={asText}
      onChange={(e) =>
        onChange(e.target.value ? Math.floor(new Date(e.target.value).getTime() / 1000) : undefined)
      }
    />
  );
}

function PickChip({ on, onClick, children }: {
  on: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`rounded-full border px-3 py-1 text-xs ${
        on
          ? "border-brand-700 bg-brand-700 text-white dark:border-sky-300 dark:bg-sky-300 dark:text-slate-900"
          : "border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

export default function Reports() {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  useTitle(t("reports.title"));
  const [loaded, setLoaded] = useState(false);
  const [reports, setReports] = useState<Report[]>([]);
  const [catalogue, setCatalogue] = useState<SectionKind[]>([]);
  const [verdicts, setVerdicts] = useState<string[]>([]);
  const [editing, setEditing] = useState<Report | "new" | null>(null);
  const [name, setName] = useState("");
  const [spec, setSpec] = useState<ReportSpec>(BLANK);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [style, setStyle] = useState<ReportStyle | null>(null);
  const [styleDefaults, setStyleDefaults] = useState<ReportStyle | null>(null);
  const [deleting, setDeleting] = useState<Report | null>(null);
  const [benches, setBenches] = useState<Bench[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [description, setDescription] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await api.reports();
      setReports(d.reports);
      setLoaded(true);
      setCatalogue(d.catalogue);
      setVerdicts(d.verdicts);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
    // The look panel is dressing, not data: when it cannot load, the report
    // list must still work, so its failure stays out of `error`.
    try {
      const s = await api.reportStyle();
      setStyle(s.style);
      setStyleDefaults(s.defaults);
    } catch {
      setStyle(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // For the bench picker. The spec has always had `scope.benches` and the
    // form never offered it, so "last 1 run" meant "on whichever rig ran last"
    // - already ambiguous across three rigs, and quietly changing meaning as
    // rigs are added.
    api.benches().then((d) => setBenches(d.benches)).catch(() => {});
    api.campaigns({ limit: 50 }).then((d) => setCampaigns(d.campaigns)).catch(() => {});
  }, []);

  useEffect(() => {
    // "Make a report of this campaign" on a campaign page lands here with the
    // campaign in the address: the editor opens on a new definition scoped to
    // it, so the reader is one preview away, not five fields.
    const asked = Number(new URLSearchParams(window.location.search).get("campaign"));
    if (asked > 0) {
      open("new");
      setSpec({ ...structuredClone(BLANK), scope: { ...structuredClone(BLANK.scope), campaign: asked } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function open(r: Report | "new") {
    setEditing(r);
    setName(r === "new" ? "" : r.name);
    setDescription(r === "new" ? "" : r.description || "");
    setSpec(r === "new" ? structuredClone(BLANK) : structuredClone(r.spec));
    setPreview("");
    setError("");
  }

  const detailsFor = (type: string) =>
    catalogue.find((c) => c.type === type)?.detail ?? [];

  function patchSection(i: number, patch: Partial<ReportSection>) {
    const next = spec.sections.map((s, j) => (i === j ? { ...s, ...patch } : s));
    setSpec({ ...spec, sections: next });
  }

  function move(i: number, by: number) {
    const next = [...spec.sections];
    const j = i + by;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setSpec({ ...spec, sections: next });
  }

  async function refreshPreview() {
    setBusy(true);
    try {
      setPreview(await api.previewReport(spec, name || t("reports.preview")));
      setError("");
    } catch (e) {
      setPreview("");
      // The server's words, not ours: it is the thing that refused, and it
      // says which section and why.
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    try {
      if (editing === "new") await api.createReport(name, description, spec);
      else if (editing) await api.updateReport(editing.id, { name, description, spec });
      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /** Only what differs from the defaults is stored, so a future default still
   *  reaches every instance that never touched that field. */
  function styleOverrides(): Partial<ReportStyle> {
    if (!style || !styleDefaults) return {};
    const out: Partial<ReportStyle> = {};
    if (style.brand !== styleDefaults.brand) out.brand = style.brand;
    if (style.strapline !== styleDefaults.strapline) out.strapline = style.strapline;
    if (style.accent !== styleDefaults.accent) out.accent = style.accent;
    const vs: ReportStyle["verdicts"] = {};
    for (const k of Object.keys(style.verdicts)) {
      const a = style.verdicts[k];
      const d = styleDefaults.verdicts[k];
      if (d && (a.fg !== d.fg || a.bg !== d.bg)) vs[k] = a;
    }
    if (Object.keys(vs).length) out.verdicts = vs;
    return out;
  }

  async function saveStyle(overrides: Partial<ReportStyle>) {
    setBusy(true);
    try {
      const r = await api.saveReportStyle(overrides);
      setStyle(r.style);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const runsLast = Array.isArray(spec.scope.runs) ? 0 : spec.scope.runs.last;
  const campaignWords = (id: number) => {
    const c = campaigns.find((x) => x.id === id);
    return c
      ? t("reports.campaignOption", {
          id: c.id,
          build: c.build_ref || t("campaign.installed"),
          bench: c.bench_name ?? c.bench_id,
          status: t(`reports.campaignStatus.${c.status}` as "reports.campaignStatus.green"),
        })
      : `#${id}`;
  };

  return (
    <section>
      {/* The page's own width, like every other page: this one sat in a
          narrower column of its own and read as a different service. */}
      <PageHead
        title={t("reports.heading")}
        answer={reports.length > 0 ? t("reports.answerSaved", { count: reports.length }) : undefined}
        after={<p className="text-sm text-slate-500">{t("reports.intro")}</p>}
        actions={
          <button className="btn-primary" onClick={() => open("new")}>
            {t("reports.newReport")}
          </button>
        }
      />
      {!loaded && <Loading label={t("common.loading")} />}

      {error && <Notice tone="error">{error}</Notice>}

      {!editing && (
        <Board as="ul">
          {reports.map((r) => (
            <li key={r.id} className="flex flex-wrap items-baseline gap-x-3 px-4 py-3">
              <button
                className="font-medium text-brand-700 hover:underline dark:text-sky-300"
                onClick={() => open(r)}
              >
                {r.name}
              </button>
              <span className="text-xs text-slate-500">
                {t("reports.sectionCount", { count: r.spec.sections.length })}
                {t("reports.edited", { when: ago(r.updated_at) })}
                {r.updated_by ? t("reports.editedBy", { who: r.updated_by }) : ""}
              </span>
              <span className="ml-auto flex gap-2 text-sm">
                <a className="text-brand-700 hover:underline dark:text-sky-300"
                   href={api.reportUrl(r.id, "html")} target="_blank" rel="noreferrer">
                  HTML
                </a>
                <a className="text-brand-700 hover:underline dark:text-sky-300"
                   href={api.reportUrl(r.id, "pdf")} target="_blank" rel="noreferrer">
                  PDF
                </a>
                {/* Asks first. A definition is shared by everyone and there is
                    no undo, and this was one unguarded click between HTML and
                    PDF - the two links either side of it are harmless, which is
                    exactly what made the third one dangerous. Not the
                    type-the-name dialog benches and runs use: this deletes a
                    definition, not a run's recorded history. */}
                <button
                  className="text-slate-500 hover:underline"
                  onClick={() => setDeleting(r)}
                >
                  {t("reports.delete")}
                </button>
              </span>
            </li>
          ))}
          {loaded && reports.length === 0 && (
            <li className="px-4 py-3 text-base text-slate-500">
              {t("reports.empty")}
            </li>
          )}
        </Board>
      )}

      {/* The look belongs to the instance, not to the definitions: when the
          brand line changes, every saved report changes with it. */}
      {/* SHOWN TO WHOEVER CAN SAVE IT, AND FOLDED. Saving needs an instance
          administrator (PUT /reports/style), while reading the style needs only
          a session - so 45 words of branding policy and three inputs were open
          on every visit for readers who would be refused at Save. */}
      {!editing && style && isAdmin && (
        <details className="mt-8 max-w-2xl rounded border border-slate-200 p-3 dark:border-slate-800">
          <summary className="cursor-pointer px-1 text-sm font-medium text-slate-900 dark:text-slate-100">
            {t("reports.lookLegend")}
          </summary>
          <p className="text-xs text-slate-500">
            {t("reports.lookNote")}
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="text-slate-500">{t("reports.brand")}</span>
              <input
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
                value={style.brand}
                onChange={(e) => setStyle({ ...style, brand: e.target.value })}
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-500">{t("reports.accent")}</span>
              <span className="mt-1 flex items-center gap-2">
                <input
                  aria-label={t("reports.accentLabel")}
                  type="color"
                  value={style.accent}
                  onChange={(e) => setStyle({ ...style, accent: e.target.value })}
                />
                <code className="text-xs text-slate-500">{style.accent}</code>
              </span>
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="text-slate-500">{t("reports.strapline")}</span>
              <input
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
                value={style.strapline}
                onChange={(e) => setStyle({ ...style, strapline: e.target.value })}
              />
            </label>
          </div>
          <details className="mt-3">
            <summary className="cursor-pointer text-sm text-slate-600 dark:text-slate-300">
              {t("reports.verdictColours")}
            </summary>
            <div className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
              {Object.keys(style.verdicts).map((k) => (
                <div key={k} className="flex items-center gap-2 text-sm">
                  <span className="w-20 text-slate-600 dark:text-slate-300">{k}</span>
                  <input
                    aria-label={t("reports.verdictFg", { verdict: k })}
                    type="color"
                    value={style.verdicts[k].fg}
                    onChange={(e) =>
                      setStyle({
                        ...style,
                        verdicts: {
                          ...style.verdicts,
                          [k]: { ...style.verdicts[k], fg: e.target.value },
                        },
                      })
                    }
                  />
                  <input
                    aria-label={t("reports.verdictBg", { verdict: k })}
                    type="color"
                    value={style.verdicts[k].bg}
                    onChange={(e) =>
                      setStyle({
                        ...style,
                        verdicts: {
                          ...style.verdicts,
                          [k]: { ...style.verdicts[k], bg: e.target.value },
                        },
                      })
                    }
                  />
                  <span
                    className="rounded px-1.5 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-wider"
                    style={{ background: style.verdicts[k].bg, color: style.verdicts[k].fg }}
                  >
                    {k}
                  </span>
                </div>
              ))}
            </div>
          </details>
          <div className="mt-3 flex gap-2">
            <button
              className="btn-primary"
              disabled={busy}
              onClick={() => void saveStyle(styleOverrides())}
            >
              {t("reports.saveLook")}
            </button>
            <button
              className="btn-quiet"
              disabled={busy}
              onClick={() => void saveStyle({})}
            >
              {t("reports.resetDefaults")}
            </button>
          </div>
        </details>
      )}

      {editing && (
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <div>
            <label className="block text-sm">
              <span className="text-slate-500">{t("reports.name")}</span>
              <input
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            <label className="mt-3 block text-sm">
              <span className="text-slate-500">
                {t("reports.whatFor")}{" "}
                <span className="text-xs">{t("reports.whatForHint")}</span>
              </span>
              <input
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>

            <fieldset className="mt-4">
              <legend className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {t("reports.scope")}
              </legend>
              {spec.scope.campaign ? (
                /* A WHOLE CAMPAIGN: its stops' runs, whatever "last N" says. */
                <p className="mt-2 text-sm" data-testid="campaign-scope">
                  <span className="text-slate-500">{t("reports.campaignScope")}</span>{" "}
                  {campaignWords(spec.scope.campaign)}
                  <button
                    type="button"
                    className="ml-2 text-xs underline"
                    onClick={() => setSpec({ ...spec, scope: { ...spec.scope, campaign: undefined } })}
                  >
                    {t("reports.campaignDrop")}
                  </button>
                </p>
              ) : (
                <label className="mt-2 block text-sm">
                  <span className="text-slate-500">{t("reports.lastRuns")}</span>
                  <input
                    type="number" min={1} max={200}
                    className="mt-1 w-24 rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
                    value={runsLast || 1}
                    onChange={(e) =>
                      setSpec({ ...spec, scope: { ...spec.scope, runs: { last: Number(e.target.value) || 1 } } })
                    }
                  />
                </label>
              )}
              {campaigns.length > 0 && (
                <label className="mt-2 block text-sm">
                  <span className="text-slate-500">{t("reports.campaign")}</span>
                  <select
                    className="mt-1 block max-w-full rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
                    value={spec.scope.campaign ?? ""}
                    onChange={(e) =>
                      setSpec({ ...spec, scope: { ...spec.scope, campaign: Number(e.target.value) || undefined } })
                    }
                  >
                    <option value="">{t("reports.campaignNone")}</option>
                    {campaigns.map((c) => (
                      <option key={c.id} value={c.id}>{campaignWords(c.id)}</option>
                    ))}
                  </select>
                </label>
              )}
              {/* WHICH RIGS. Absent until now, though the format has always
                  taken it: an unscoped report is "the last N runs anywhere",
                  which was already ambiguous across three rigs. */}
              <div className="mt-2 text-sm">
                <span className="text-slate-500">{t("reports.benches")}</span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <PickChip
                    on={(spec.scope.benches ?? []).length === 0}
                    onClick={() => setSpec({ ...spec, scope: { ...spec.scope, benches: [] } })}
                  >
                    {t("reports.any")}
                  </PickChip>
                  {benches.map((b) => {
                    const picked = (spec.scope.benches ?? []).includes(b.id);
                    return (
                      <PickChip
                        key={b.id}
                        on={picked}
                        onClick={() =>
                          setSpec({
                            ...spec,
                            scope: {
                              ...spec.scope,
                              benches: picked
                                ? (spec.scope.benches ?? []).filter((x) => x !== b.id)
                                : [...(spec.scope.benches ?? []), b.id],
                            },
                          })
                        }
                      >
                        {b.name}
                      </PickChip>
                    );
                  })}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-sm">
                <label>
                  <span className="text-slate-500">{t("reports.from")}</span>
                  <DateBox
                    value={spec.scope.from}
                    onChange={(v) => setSpec({ ...spec, scope: { ...spec.scope, from: v } })}
                  />
                </label>
                <label>
                  <span className="text-slate-500">{t("reports.to")}</span>
                  <DateBox
                    value={spec.scope.to}
                    onChange={(v) => setSpec({ ...spec, scope: { ...spec.scope, to: v } })}
                  />
                </label>
              </div>
              <label className="mt-2 block text-sm">
                <span className="text-slate-500">{t("reports.casesMatching")}</span>
                <input
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
                  value={spec.scope.scenarios}
                  onChange={(e) =>
                    setSpec({ ...spec, scope: { ...spec.scope, scenarios: e.target.value } })
                  }
                />
              </label>
              <div className="mt-2 text-sm text-slate-500">
                {t("reports.verdicts")}{" "}
                <span className="text-xs">
                  <Trans
                    i18nKey="reports.verdictsHint"
                    components={{ code: <code /> }}
                  />
                </span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm">
                {verdicts.map((v) => (
                  <label key={v} className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={spec.scope.verdicts.includes(v)}
                      onChange={(e) =>
                        setSpec({
                          ...spec,
                          scope: {
                            ...spec.scope,
                            verdicts: e.target.checked
                              ? [...spec.scope.verdicts, v]
                              : spec.scope.verdicts.filter((x) => x !== v),
                          },
                        })
                      }
                    />
                    {v}
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-4">
              <legend className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {t("reports.sectionsLegend")}
              </legend>
              <ul className="mt-2 space-y-2">
                {spec.sections.map((s, i) => (
                  <li key={i} className="rounded border border-slate-200 p-2 dark:border-slate-800">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <select
                        aria-label={t("reports.sectionType")}
                        className="rounded border border-slate-300 px-1 py-0.5 dark:border-slate-700 dark:bg-slate-900"
                        value={s.type}
                        onChange={(e) =>
                          patchSection(i, {
                            type: e.target.value,
                            detail: detailsFor(e.target.value)[0] || "",
                          })
                        }
                      >
                        {catalogue.map((c) => (
                          <option key={c.type} value={c.type}>{c.type}</option>
                        ))}
                      </select>
                      <select
                        aria-label={t("reports.detail")}
                        className="rounded border border-slate-300 px-1 py-0.5 dark:border-slate-700 dark:bg-slate-900"
                        value={s.detail}
                        onChange={(e) => patchSection(i, { detail: e.target.value })}
                      >
                        {detailsFor(s.type).map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                      {catalogue.find((c) => c.type === s.type)?.layout && (
                        <select
                          aria-label={t("reports.layout")}
                          className="rounded border border-slate-300 px-1 py-0.5 dark:border-slate-700 dark:bg-slate-900"
                          value={s.layout || "grouped"}
                          onChange={(e) => patchSection(i, { layout: e.target.value })}
                        >
                          {catalogue.find((c) => c.type === s.type)!.layout!.map((l) => (
                            <option key={l} value={l}>{l}</option>
                          ))}
                        </select>
                      )}
                      <span className="text-xs text-slate-500">
                        {catalogue.find((c) => c.type === s.type)?.what}
                      </span>
                      <span className="ml-auto flex gap-1">
                        <button aria-label={t("reports.moveUp")} className="px-1 text-slate-500"
                                onClick={() => move(i, -1)}>↑</button>
                        <button aria-label={t("reports.moveDown")} className="px-1 text-slate-500"
                                onClick={() => move(i, 1)}>↓</button>
                        <button aria-label={t("reports.removeSection")} className="px-1 text-slate-500"
                                onClick={() =>
                                  setSpec({ ...spec, sections: spec.sections.filter((_, j) => j !== i) })}>
                          ×
                        </button>
                      </span>
                    </div>
                    {s.type === "notes" && (
                      <textarea
                        aria-label={t("reports.notesText")}
                        className="mt-2 w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
                        rows={3}
                        value={s.text || ""}
                        onChange={(e) => patchSection(i, { text: e.target.value })}
                      />
                    )}
                  </li>
                ))}
              </ul>
              <button
                className="mt-2 rounded border border-slate-300 px-2 py-0.5 text-sm dark:border-slate-700"
                onClick={() =>
                  setSpec({
                    ...spec,
                    sections: [...spec.sections,
                               { type: catalogue[0]?.type || "summary",
                                 detail: catalogue[0]?.detail[0] || "counts" }],
                  })
                }
              >
                {t("reports.addSection")}
              </button>
            </fieldset>

            <div className="mt-4 flex gap-2">
              <button
                className="btn-primary"
                disabled={busy || !name.trim()}
                onClick={save}
              >
                {t("common.save")}
              </button>
              <button className="btn-quiet"
                      disabled={busy} onClick={refreshPreview}>
                {t("reports.preview")}
              </button>
              <button className="rounded px-3 py-1 text-sm text-slate-500"
                      onClick={() => setEditing(null)}>
                {t("common.cancel")}
              </button>
            </div>
          </div>

          {/* The preview is rendered by the SAME builder as the saved report,
              so what is shown here is what will be produced. */}
          <div className="rounded border border-slate-200 dark:border-slate-800">
            {preview ? (
              <iframe title={t("reports.previewTitle")} className="h-[36rem] w-full bg-white"
                      srcDoc={preview} />
            ) : (
              <p className="p-4 text-sm text-slate-500">
                {t("reports.previewPrompt")}
              </p>
            )}
          </div>
        </div>
      )}

      {deleting && (
        <Modal
          title={t("reports.deleteTitle", { name: deleting.name })}
          onClose={() => setDeleting(null)}
        >
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t("reports.deleteWarn")}
          </p>
          <div className="mt-3 flex justify-end gap-2">
            <button className="px-3 py-1.5 text-sm" onClick={() => setDeleting(null)}>
              {t("reports.keepIt")}
            </button>
            <button
              className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.deleteReport(deleting.id);
                  setDeleting(null);
                  await load();
                } catch (e) {
                  setError(e instanceof ApiError ? e.message : String(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t("common.delete")}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
